/**
 * Push Notifications Hook
 *
 * Comprehensive hook for managing browser push notifications
 * - Checks if push is supported
 * - Manages permission state (default, granted, denied)
 * - Handles subscription/unsubscription
 * - Syncs with backend
 * - Provides device management
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  PushPermissionState,
  PushSubscriptionRecord,
  UsePushNotificationsReturn,
  getDeviceInfo,
} from '../types/pushNotification';
import {
  pushNotificationsApi,
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  getCurrentPushSubscription,
  createPushSubscription,
  removePushSubscription,
} from '../services/pushNotificationsApi';

/**
 * Hook for managing push notifications
 */
export const usePushNotifications = (): UsePushNotificationsReturn => {
  // State
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<PushPermissionState>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [subscriptions, setSubscriptions] = useState<PushSubscriptionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const isMountedRef = useRef(true);
  const initializingRef = useRef(false);

  /**
   * Clear any error state
   */
  const clearError = useCallback(() => {
    if (isMountedRef.current) {
      setError(null);
    }
  }, []);

  /**
   * Refresh list of subscriptions from server
   */
  const refreshSubscriptions = useCallback(async () => {
    try {
      const serverSubscriptions = await pushNotificationsApi.getSubscriptions();
      if (isMountedRef.current) {
        setSubscriptions(serverSubscriptions);
      }
    } catch (err) {
      console.error('[usePushNotifications] Failed to refresh subscriptions:', err);
    }
  }, []);

  /**
   * Check current subscription status
   */
  const checkSubscriptionStatus = useCallback(async () => {
    try {
      const currentSubscription = await getCurrentPushSubscription();
      if (isMountedRef.current) {
        setSubscription(currentSubscription);
        setIsSubscribed(!!currentSubscription);
      }

      // Also verify with server if we have a subscription
      if (currentSubscription) {
        const status = await pushNotificationsApi.checkSubscriptionStatus(
          currentSubscription.endpoint
        );
        if (!status.isActive) {
          // Subscription exists locally but not on server - consider it unsubscribed
          console.warn(
            '[usePushNotifications] Local subscription not found on server'
          );
        }
      }
    } catch (err) {
      console.error(
        '[usePushNotifications] Failed to check subscription status:',
        err
      );
    }
  }, []);

  /**
   * Initialize push notification state
   */
  const initialize = useCallback(async () => {
    if (initializingRef.current) return;
    initializingRef.current = true;

    try {
      // Check browser support
      const supported = isPushSupported();
      if (isMountedRef.current) {
        setIsSupported(supported);
      }

      if (!supported) {
        console.warn('[usePushNotifications] Push notifications not supported');
        initializingRef.current = false;
        return;
      }

      // Get current permission state
      const currentPermission = getNotificationPermission() as PushPermissionState;
      if (isMountedRef.current) {
        setPermission(currentPermission);
      }

      // Check current subscription
      await checkSubscriptionStatus();

      // Fetch subscriptions from server if logged in
      await refreshSubscriptions();
    } catch (err) {
      console.error('[usePushNotifications] Initialization error:', err);
    } finally {
      initializingRef.current = false;
    }
  }, [checkSubscriptionStatus, refreshSubscriptions]);

  /**
   * Request notification permission
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      if (isMountedRef.current) {
        setError('Push notifications are not supported in this browser');
      }
      return false;
    }

    try {
      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      const newPermission = await requestNotificationPermission();
      if (isMountedRef.current) {
        setPermission(newPermission as PushPermissionState);
      }

      return newPermission === 'granted';
    } catch (err) {
      console.error('[usePushNotifications] Permission request error:', err);
      if (isMountedRef.current) {
        setError('Failed to request notification permission. Please try again.');
      }
      return false;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [isSupported]);

  /**
   * Subscribe to push notifications
   */
  const subscribe = useCallback(
    async (deviceName?: string): Promise<boolean> => {
      if (!isSupported) {
        if (isMountedRef.current) {
          setError('Push notifications are not supported');
        }
        return false;
      }

      try {
        if (isMountedRef.current) {
          setLoading(true);
          setError(null);
        }

        // Request permission if needed
        let currentPermission = permission;
        if (currentPermission !== 'granted') {
          const granted = await requestPermission();
          if (!granted) {
            if (isMountedRef.current) {
              setError('Notification permission was denied');
            }
            return false;
          }
          currentPermission = 'granted';
        }

        // Get VAPID public key from server
        const vapidKey = await pushNotificationsApi.getVapidPublicKey();

        // Create browser subscription
        const newSubscription = await createPushSubscription(vapidKey);
        if (!newSubscription) {
          if (isMountedRef.current) {
            setError('Failed to create push subscription');
          }
          return false;
        }

        // Save to server
        const deviceInfo = getDeviceInfo();
        await pushNotificationsApi.subscribe(
          newSubscription,
          deviceName || deviceInfo.name
        );

        // Update state
        if (isMountedRef.current) {
          setSubscription(newSubscription);
          setIsSubscribed(true);
        }

        // Refresh subscriptions list
        await refreshSubscriptions();

        return true;
      } catch (err) {
        console.error('[usePushNotifications] Subscribe error:', err);
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to enable push notifications';
        if (isMountedRef.current) {
          setError(errorMessage);
        }
        return false;
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [isSupported, permission, requestPermission, refreshSubscriptions]
  );

  /**
   * Unsubscribe from push notifications on current device
   */
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!subscription) {
      return true; // Already unsubscribed
    }

    try {
      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      // Remove from server first
      try {
        await pushNotificationsApi.unsubscribe(subscription.endpoint);
      } catch (err) {
        console.warn('[usePushNotifications] Server unsubscribe failed:', err);
        // Continue with browser unsubscribe anyway
      }

      // Remove from browser
      const success = await removePushSubscription();

      if (success) {
        if (isMountedRef.current) {
          setSubscription(null);
          setIsSubscribed(false);
        }
        await refreshSubscriptions();
        return true;
      } else {
        if (isMountedRef.current) {
          setError('Failed to unsubscribe from push notifications');
        }
        return false;
      }
    } catch (err) {
      console.error('[usePushNotifications] Unsubscribe error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to disable push notifications';
      if (isMountedRef.current) {
        setError(errorMessage);
      }
      return false;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [subscription, refreshSubscriptions]);

  /**
   * Remove a device subscription from server (for managing other devices)
   */
  const removeDevice = useCallback(
    async (endpoint: string): Promise<boolean> => {
      try {
        if (isMountedRef.current) {
          setLoading(true);
          setError(null);
        }

        // Find the subscription record
        const record = subscriptions.find((s) => s.endpoint === endpoint);
        if (record) {
          await pushNotificationsApi.removeSubscription(record.id);
        } else {
          await pushNotificationsApi.unsubscribe(endpoint);
        }

        // Refresh list
        await refreshSubscriptions();

        // If this was the current device, update local state
        if (subscription?.endpoint === endpoint) {
          await removePushSubscription();
          if (isMountedRef.current) {
            setSubscription(null);
            setIsSubscribed(false);
          }
        }

        return true;
      } catch (err) {
        console.error('[usePushNotifications] Remove device error:', err);
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to remove device';
        if (isMountedRef.current) {
          setError(errorMessage);
        }
        return false;
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [subscription, subscriptions, refreshSubscriptions]
  );

  /**
   * Send a test notification to verify push is working
   */
  const sendTestNotification = useCallback(async (): Promise<boolean> => {
    if (!isSubscribed || !subscription) {
      if (isMountedRef.current) {
        setError('Not subscribed to push notifications');
      }
      return false;
    }

    try {
      if (isMountedRef.current) {
        setLoading(true);
        setError(null);
      }

      const result = await pushNotificationsApi.sendTestNotification();
      return result.success;
    } catch (err) {
      console.error('[usePushNotifications] Test notification error:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to send test notification';
      if (isMountedRef.current) {
        setError(errorMessage);
      }
      return false;
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [isSubscribed, subscription]);

  /**
   * Listen for permission changes
   */
  useEffect(() => {
    if (!isSupported) return;

    // Check if permission API is available
    if ('permissions' in navigator) {
      navigator.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((permissionStatus) => {
          const handleChange = () => {
            const newPermission = Notification.permission as PushPermissionState;
            if (isMountedRef.current) {
              setPermission(newPermission);

              // If permission was revoked, update subscription state
              if (newPermission === 'denied') {
                setSubscription(null);
                setIsSubscribed(false);
              }
            }
          };

          permissionStatus.addEventListener('change', handleChange);
          return () =>
            permissionStatus.removeEventListener('change', handleChange);
        })
        .catch(() => {
          // Permission API not fully supported, fall back to polling
        });
    }
  }, [isSupported]);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    isMountedRef.current = true;
    initialize();

    return () => {
      isMountedRef.current = false;
    };
  }, [initialize]);

  return {
    isSupported,
    permission,
    isSubscribed,
    subscription,
    subscriptions,
    loading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    removeDevice,
    sendTestNotification,
    refreshSubscriptions,
    clearError,
  };
};

export default usePushNotifications;
